import React, { FC, memo, useMemo } from 'react';
import { StatusTip } from 'tea-component';
import { TimePeriodType } from '../../types';
import { newMathFormat } from '@/pages/intelligence/utils/commonUtils';
import { useIntl, getLocale } from 'umi';

import { GameSegmentOptions } from '../../_config/constant';
const { LoadingTip } = StatusTip;
import './index.scss';

interface SummeryProps {
  summaryData: any[];
  segment: string;
  timePeriod: TimePeriodType;
}

const Summary: FC<SummeryProps> = (props) => {
  const { segment, timePeriod, summaryData } = props;

  const { formatMessage } = useIntl();
  const locale = getLocale();
  const subTitle = useMemo(() => {
    const gameType = GameSegmentOptions?.find((item) => item.value === segment);
    const gameName = gameType ? formatMessage({ id: gameType?.text }) : segment;
    if (locale === 'zh-CN') {
      return (
        <>
          对于 {timePeriod === '1y~5y' ? '五年' : '十二个月'}内的
          <span className="highlight">{gameName}</span>
          类游戏 :
        </>
      );
    }

    return (
      <>
        For <span className="highlight">{gameName} games</span>, within a{}{' '}
        {timePeriod === '1y~5y' ? 'five-year' : 'twelve-months'} period:
      </>
    );
  }, [locale, segment, timePeriod]);

  const renderSummaryContent = (list: any[]) => {
    if (!Array.isArray(list)) return null;
    const gameType = GameSegmentOptions?.find((item) => item.value === segment);
    const gameName = gameType ? formatMessage({ id: gameType?.text }) : segment;
    return list?.map((item: any) => {
      if (locale === 'zh-CN') {
        const trendContent = (
          <>
            {timePeriod === '1y~5y' ? 'Y1~Y5' : 'M1~M12'}的年度{formatMessage({
              id: `dbi_benchmark_${item?.type}_mobile`,
            })}趋势为{' '}
            <span className="highlight">{item?.trend}</span>。
          </>
        );
        const trendEmpty = `${formatMessage({
          id: `dbi_benchmark_${item?.type}_mobile`,
        })}游戏数据可能丢失或不可用。`;
       
        const averageContent = (
          <>
            各游戏平均总
            {formatMessage({
              id: `dbi_benchmark_${item?.type}_mobile`,
            })}
            为{' '}
            <span className="highlight">
              {item?.type === 'revenue' ? '$' : null}
              {newMathFormat(item?.average)}
            </span>
            ，
          </>
        );

        const regionContent = getRegionContent(item?.region_cn);
        const targetTrendContent = item?.trend ? trendContent : trendEmpty;
        return (
          <div className="summary-content">
            {targetTrendContent}
            {averageContent}
            {regionContent}
          </div>
        );
      } else {
        const trendContent = (
          <>
            The relative {item?.type === 'dau' ? 'avg.DAU' : item?.type} trend
            of {timePeriod === '1y~5y' ? 'Y1~Y5' : 'M1~M12'} is{' '}
            <span className="highlight">{item?.trend}</span>.
          </>
        );
        const trendEmpty = `${
          item?.type === 'dau' ? 'avg.DAU' : item?.type
        } data of ${gameName} games may be missing/unavailable.`;

        const averageContent = (
          <>
            {' '}
            Total {item?.type === 'dau' ? 'avg.DAU' : item?.type} is{' '}
            <span className="highlight">
              {' '}
              {item?.type === 'revenue' ? '$' : null}
              {newMathFormat(item?.average)}
            </span>{' '}
            in average,
          </>
        );

        const regionContent = getRegionContent(item?.region_en);
        const targetTrendContent = item?.trend ? trendContent : trendEmpty;
        return (
          <div className="summary-content">
            {targetTrendContent}
            {averageContent}
            {regionContent}
          </div>
        );
      }
    });
  };

  const getRegionContent = (region: string[]) => {
    if (!Array.isArray(region)) return null;
    if (!region.length) return null;
    if (locale === 'zh-CN') {
      if (region.length === 1) {
        return (
          <>
            主要分布于
            <span className="highlight">{region[0]}</span>。
          </>
        );
      }

      if (region.length === 2) {
        return (
          <>
            主要分布于
            <span className="highlight">{region[0]}</span> 和{' '}
            <span className="highlight">{region[1]}</span>。
          </>
        );
      }

      if (region.length === 3) {
        return (
          <>
            主要分布于
            <span className="highlight">{region[0]}</span>,{' '}
            <span className="highlight">{region[1]}</span> 和{' '}
            <span className="highlight">{region[2]}</span>。
          </>
        );
      }
    } else {
      if (region.length === 1) {
        return (
          <>
            which is mainly distributed in{' '}
            <span className="highlight">{region[0]}</span>.
          </>
        );
      }

      if (region.length === 2) {
        return (
          <>
            which is mainly distributed in{' '}
            <span className="highlight">{region[0]}</span> and{' '}
            <span className="highlight">{region[1]}</span>.
          </>
        );
      }

      if (region.length === 3) {
        return (
          <>
            {' '}
            which is mainly distributed in{' '}
            <span className="highlight">{region[0]}</span>,{' '}
            <span className="highlight">{region[1]}</span> and{' '}
            <span className="highlight">{region[2]}</span>.
          </>
        );
      }
    }
  };

  return (
    <div className="dbi-benchmark-summary-kpi">
      <div className="dbi-benchmark-summary-kpi-title">
        {formatMessage({ id: 'dbi_benchmark_summary_kpi_data' })}
      </div>
      <>
        {summaryData?.length > 0 ? (
          <>
            <div className="summary-sub-title">{subTitle}</div>
            {renderSummaryContent(summaryData)}
          </>
        ) : (
          <div className="revenue-nodata-container">
            {formatMessage({ id: 'dbi_detail_no_data_under' })}
          </div>
        )}
      </>
    </div>
  );
};

Summary.displayName = 'Summary';

Summary.defaultProps = {
  summaryData: [],
};
export default memo(Summary);
